import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Loader2, Save, Thermometer, MessageCircleReply, Flame, Snowflake, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Outreach Automation → Tuning tab
 *
 * Two cards:
 *   1. Lead Temperature thresholds — when does the HOT chip fire?
 *      Backed by GET/PUT /api/smb/outreach/lead-temperature-config
 *   2. Auto-reply detection — phrases + windows that prevent WhatsApp
 *      Business auto-greetings from being treated as a real human reply.
 *      Backed by GET/PUT /api/smb/outreach/auto-reply-config
 *
 * Why this tab exists (Feb 2026): multiple SMB leads were showing up as
 * HOT off a single accidental tap on the preview link, AND
 * "Thank you for contacting…" auto-replies landing >5 min after our send
 * were being mis-classified as human replies, flipping the HOT chip.
 * Both knobs are now live-tunable from the UI without a redeploy.
 */
export default function OutreachTuningTab() {
  const [tempCfg, setTempCfg] = useState(null);
  const [arCfg, setArCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingTemp, setSavingTemp] = useState(false);
  const [savingAr, setSavingAr] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a] = await Promise.all([
        api.get('/smb/outreach/lead-temperature-config'),
        api.get('/smb/outreach/auto-reply-config'),
      ]);
      setTempCfg(t.data?.data || null);
      setArCfg(a.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tuning config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveTemp = async () => {
    if (!tempCfg) return;
    setSavingTemp(true);
    try {
      const r = await api.put('/smb/outreach/lead-temperature-config', {
        hot_min_total_clicks: Number(tempCfg.hot_min_total_clicks) || 1,
        hot_distinct_msgs: Number(tempCfg.hot_distinct_msgs) || 1,
        hot_repeat_clicks: Number(tempCfg.hot_repeat_clicks) || 1,
      });
      setTempCfg(r.data?.data || tempCfg);
      toast.success('Lead temperature thresholds saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingTemp(false);
    }
  };

  const saveAr = async () => {
    if (!arCfg) return;
    setSavingAr(true);
    try {
      const payload = {
        enabled: !!arCfg.enabled,
        time_window_sec: Number(arCfg.time_window_sec) || 60,
        phrase_window_sec: Number(arCfg.phrase_window_sec) || 300,
        phrases: (arCfg.phrases || []).map(s => (s || '').trim()).filter(Boolean),
        strong_phrases: (arCfg.strong_phrases || []).map(s => (s || '').trim()).filter(Boolean),
      };
      const r = await api.put('/smb/outreach/auto-reply-config', payload);
      setArCfg(r.data?.data || arCfg);
      toast.success('Auto-reply detection saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingAr(false);
    }
  };

  if (loading || !tempCfg || !arCfg) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading tuning settings…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="outreach-tuning-tab">
      {/* -------- Lead Temperature Card -------- */}
      <Card data-testid="lead-temperature-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Thermometer className="h-4 w-4 text-orange-500" />
                Lead Temperature Thresholds
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                When does a lead show the <Flame className="h-3 w-3 inline text-red-500" /> HOT chip? Tune these to balance signal vs. noise.
                One click on M1/M2 stays <span className="font-medium text-amber-500">WARM</span> until the second click promotes it.
              </CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={fetchAll} title="Refresh" data-testid="tuning-refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="hot_min_total_clicks">Min total clicks for HOT</Label>
              <Input
                id="hot_min_total_clicks"
                type="number" min={1} max={20}
                value={tempCfg.hot_min_total_clicks}
                onChange={(e) => setTempCfg({ ...tempCfg, hot_min_total_clicks: e.target.value })}
                data-testid="hot-min-clicks"
              />
              <p className="text-xs text-muted-foreground">
                Default <strong>2</strong>. A single tap → WARM. Two+ taps on M1/M2 → HOT.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hot_distinct_msgs">Distinct messages clicked</Label>
              <Input
                id="hot_distinct_msgs"
                type="number" min={1} max={5}
                value={tempCfg.hot_distinct_msgs}
                onChange={(e) => setTempCfg({ ...tempCfg, hot_distinct_msgs: e.target.value })}
                data-testid="hot-distinct-msgs"
              />
              <p className="text-xs text-muted-foreground">
                Default <strong>2</strong>. Clicking links from 2 different sends → HOT (independent shortcut).
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="hot_repeat_clicks">Repeat-click shortcut</Label>
              <Input
                id="hot_repeat_clicks"
                type="number" min={1} max={50}
                value={tempCfg.hot_repeat_clicks}
                onChange={(e) => setTempCfg({ ...tempCfg, hot_repeat_clicks: e.target.value })}
                data-testid="hot-repeat-clicks"
              />
              <p className="text-xs text-muted-foreground">
                Default <strong>5</strong>. N+ clicks anywhere → HOT.
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveTemp} disabled={savingTemp} data-testid="save-temp-cfg">
              {savingTemp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save thresholds
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* -------- Auto-Reply Detection Card -------- */}
      <Card data-testid="auto-reply-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageCircleReply className="h-4 w-4 text-emerald-500" />
            Auto-Reply Detection
          </CardTitle>
          <CardDescription className="text-xs mt-1">
            Detect WhatsApp Business auto-greetings (&quot;Thank you for contacting…&quot;, &quot;Out of office&quot;) so they don&apos;t pause the outreach cadence
            and don&apos;t <Snowflake className="h-3 w-3 inline text-sky-400" /> falsely flip the lead to HOT.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Enable auto-reply detection</Label>
              <p className="text-xs text-muted-foreground">Disabling treats every inbound as a real human reply.</p>
            </div>
            <Switch
              checked={!!arCfg.enabled}
              onCheckedChange={(v) => setArCfg({ ...arCfg, enabled: v })}
              data-testid="ar-enabled"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="time_window_sec">Time window (sec)</Label>
              <Input
                id="time_window_sec"
                type="number" min={0}
                value={arCfg.time_window_sec}
                onChange={(e) => setArCfg({ ...arCfg, time_window_sec: e.target.value })}
                data-testid="ar-time-window"
              />
              <p className="text-xs text-muted-foreground">
                Inbound within N seconds of our send → auto-reply (default <strong>60</strong>).
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phrase_window_sec">Phrase-match window (sec)</Label>
              <Input
                id="phrase_window_sec"
                type="number" min={0}
                value={arCfg.phrase_window_sec}
                onChange={(e) => setArCfg({ ...arCfg, phrase_window_sec: e.target.value })}
                data-testid="ar-phrase-window"
              />
              <p className="text-xs text-muted-foreground">
                Phrase match must arrive within N seconds (default <strong>86400</strong> = 24h).
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="strong_phrases">
              Strong phrases <span className="text-emerald-500 text-xs">(bypass time gate)</span>
            </Label>
            <Textarea
              id="strong_phrases"
              rows={4}
              value={(arCfg.strong_phrases || []).join('\n')}
              onChange={(e) => setArCfg({
                ...arCfg,
                strong_phrases: e.target.value.split('\n').map(s => s.trim()),
              })}
              placeholder="thank you for contacting&#10;out of office&#10;automated message"
              data-testid="ar-strong-phrases"
            />
            <p className="text-xs text-muted-foreground">
              One phrase per line. ANY match flags the inbound as auto-reply regardless of how long after our send it arrives.
              Use sparingly — only phrases that are categorically system-generated.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phrases">Regular phrases</Label>
            <Textarea
              id="phrases"
              rows={6}
              value={(arCfg.phrases || []).join('\n')}
              onChange={(e) => setArCfg({
                ...arCfg,
                phrases: e.target.value.split('\n').map(s => s.trim()),
              })}
              placeholder="we will get back to you&#10;our business hours"
              data-testid="ar-phrases"
            />
            <p className="text-xs text-muted-foreground">
              One phrase per line. Match only counts as auto-reply if the inbound arrives within the phrase-match window.
            </p>
          </div>

          <div className="flex justify-end">
            <Button onClick={saveAr} disabled={savingAr} data-testid="save-ar-cfg">
              {savingAr ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save auto-reply config
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
